import React, { useState, useEffect } from 'react'
import classnames from 'classnames'
import map from 'lodash/map'
import filter from 'lodash/filter'
import SiteCheckbox from '@SiteComponents/SiteCheckbox'

import {
  TSpeakerVoiceUsedLanguages,
} from '@SiteModules/SpeakerVoice/types'

import './LanguagesManager.scss'

interface LanguagesManagerProps {
  readonly usedLanguages: TSpeakerVoiceUsedLanguages
  readonly languagesNames: string[]
  readonly selectedLanguagesNames: string[]
  changeSelectedLanguageNames(e: React.ChangeEvent<HTMLInputElement>, languageName: string): void
}

const LanguagesManager: React.FC<LanguagesManagerProps> = props => {
  const {
    usedLanguages,
    languagesNames,
    selectedLanguagesNames,
  } = props

  const [languageSearchText, setLanguageSearchText] = useState('')
  const [relevantLanguages, setRelevantLanguages] = useState(usedLanguages)
  const [showCheckbox, setShowCheckbox] = useState<string | null>(Object.keys(relevantLanguages)[0] || null)

  const numberOfRelevantLanguages = Object.keys(relevantLanguages).length
  const hasNoRelevantLanguages = numberOfRelevantLanguages === 0
  const shouldShowDropdown = !hasNoRelevantLanguages

  useEffect(() => {
    const normalizedSearch = languageSearchText.trim().toLowerCase()

    if (normalizedSearch.length === 0) {
      setRelevantLanguages(usedLanguages)
      return
    }

    const relevantLanguagesNames = filter(languagesNames, (name) => {
      const normalizedName = name.toLowerCase()
      return normalizedName.includes(normalizedSearch)
    })

    const relevantLanguagesNamesSet = new Set(relevantLanguagesNames)

    const newRelevantLanguages = Object.fromEntries(
      Object.entries(usedLanguages).filter(([name]) => relevantLanguagesNamesSet.has(name))
    )

    setRelevantLanguages(newRelevantLanguages)
  }, [usedLanguages, languagesNames, languageSearchText])

  useEffect(() => {
    if (typeof(showCheckbox) !== 'number' && numberOfRelevantLanguages > 0) {
      setShowCheckbox(Object.keys(relevantLanguages)[0] || null)
    }
  }, [numberOfRelevantLanguages])

  const handleLanguageSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = (e.target as HTMLInputElement).value
    setLanguageSearchText(searchText)
  }

  const languageItems = map(Object.entries(relevantLanguages), ([name, useCount]) => {
    const selected = selectedLanguagesNames.includes(name)
    const shouldShowCheckbox = selected || showCheckbox === name

    const displayCheckbox = () => {
      setShowCheckbox(name)
    }

    const handleLabelSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
      props.changeSelectedLanguageNames(e, name)
    }

    return (
      <li
        key={name}
        className={classnames(
          'LanguagesManager__dropdown-item',
          { 'LanguagesManager__dropdown-item-show-checkbox': showCheckbox === name }
        )}
        onMouseEnter={displayCheckbox}
      >
        {shouldShowCheckbox && (
          <SiteCheckbox
            id={name}
            checkboxValue={selected ? 'checked' : 'unchecked'}
            onChange={handleLabelSelection}
          />
        )}
        <label
          className="LanguagesManager__dropdown-item-label"
          htmlFor={`language_${name}`}
        >
          <div className="LanguagesManager__dropdown-item-caption">
            {name}
          </div>
          <div className="LanguagesManager__dropdown-item-voices-used">
            {useCount === 1 ? '1 voice' : `${useCount} voices`}
          </div>
        </label>
      </li>
    )
  })

  return (
    <div
      className="LanguagesManager"
    >
      <div className={classnames(
        'LanguagesManager__input-wrapper',
        { 'LanguagesManager__input-wrapper-labels': !hasNoRelevantLanguages },
        { 'LanguagesManager__input-wrapper-no-labels': hasNoRelevantLanguages }
      )}>
        <input
          placeholder="Select..."
          autoComplete="off"
          value={languageSearchText}
          onChange={handleLanguageSearchTextChange}
        />
      </div>
      {shouldShowDropdown && (
        <ul className="LanguagesManager__dropdown-items">
          {languageItems}
        </ul>
      )}
    </div>
  )
}

export default LanguagesManager
